import type { PayoutPaymentBatch } from '@/services/payoutPaymentService';
import { payoutSettlement } from '@/utils/compensation/paymentCalculations';

export interface PayoutPaymentEmailPayout {
  id: string;
  netPayout: number;
  paymentAmount: number;
  recapturedAmount?: number;
  splits?: string | null;
}

export interface PayoutPaymentEmailAllocationPreview {
  payoutId: string;
  loanNumber: string;
  borrowerName: string | null;
  payoutType: string;
  isVolumeBonus: boolean;
  amountOwed: number;
  thisPayment: number;
  totalPaid: number;
  recapturedAmount: number;
  remainingBalance: number;
}

export interface PayoutPaymentEmailPreview {
  batchId: string;
  recipientName: string;
  checkNumber: string;
  paymentDate: string;
  totalAmount: number;
  totalOwed: number;
  totalPaid: number;
  recapturedAmount: number;
  remainingBalance: number;
  allocations: PayoutPaymentEmailAllocationPreview[];
  paymentEmailSentAt: string | null;
  paymentEmailSentByName: string | null;
  paymentEmailRecipient: string | null;
}

function roundCurrency(value: number): number {
  return Math.round((value + Number.EPSILON) * 100) / 100;
}

/**
 * Builds the saved-check summary shown before an LO email is sent. The check
 * allocation is historical; total paid and remaining balance are current so a
 * delayed send never tells the LO that an already-paid balance is still due.
 */
export function buildPayoutPaymentEmailPreview(
  targetBatch: PayoutPaymentBatch,
  payouts: PayoutPaymentEmailPayout[]
): PayoutPaymentEmailPreview | null {
  if (targetBatch.voidedAt || !targetBatch.historyComplete) {
    return null;
  }

  const payoutsById = new Map(payouts.map((payout) => [payout.id, payout]));

  const allocations: PayoutPaymentEmailAllocationPreview[] = [];
  for (const allocation of targetBatch.allocations) {
    const payoutId = allocation.payoutId || allocation.payoutIdSnapshot;
    const payout = payoutsById.get(payoutId);
    if (!payout) {
      return null;
    }

    const settlement = payoutSettlement(payout.netPayout, payout.paymentAmount, payout.recapturedAmount);
    const totalPaid = settlement.cash;
    const amountOwed = settlement.net;

    allocations.push({
      payoutId,
      loanNumber: allocation.loanNumber,
      borrowerName: allocation.borrowerName,
      payoutType: allocation.payoutType === 'volume_bonus' ? payout.splits || 'Volume Bonus' : allocation.payoutType,
      isVolumeBonus: allocation.payoutType === 'volume_bonus',
      amountOwed,
      thisPayment: roundCurrency(allocation.amount),
      totalPaid,
      recapturedAmount: settlement.recaptured,
      remainingBalance: settlement.remaining
    });
  }

  return {
    batchId: targetBatch.id,
    recipientName: targetBatch.recipientName,
    checkNumber: targetBatch.checkNumber,
    paymentDate: targetBatch.paymentDate,
    totalAmount: roundCurrency(targetBatch.totalAmount),
    totalOwed: roundCurrency(allocations.reduce((sum, allocation) => sum + allocation.amountOwed, 0)),
    totalPaid: roundCurrency(allocations.reduce((sum, allocation) => sum + allocation.totalPaid, 0)),
    recapturedAmount: roundCurrency(allocations.reduce((sum, allocation) => sum + allocation.recapturedAmount, 0)),
    remainingBalance: roundCurrency(allocations.reduce((sum, allocation) => sum + allocation.remainingBalance, 0)),
    allocations,
    paymentEmailSentAt: targetBatch.paymentEmailSentAt,
    paymentEmailSentByName: targetBatch.paymentEmailSentByName,
    paymentEmailRecipient: targetBatch.paymentEmailRecipient
  };
}
